import { Navigation, MapPin, Compass, User, Video, Eye, Wallet, Banknote } from "lucide-react";

/* Stylised geographic map (sea + land + city labels), self-contained SVG */
const MiniMap = () => (
  <svg viewBox="0 0 320 175" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
    <defs>
      <linearGradient id="sea" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="#1e4468" />
        <stop offset="100%" stopColor="#14304c" />
      </linearGradient>
      <linearGradient id="land" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#4f7a4a" />
        <stop offset="100%" stopColor="#3c5f3a" />
      </linearGradient>
    </defs>
    <rect width="320" height="175" fill="url(#sea)" />
    {/* Ireland */}
    <path d="M8,58 C2,72 6,92 20,96 C30,90 30,70 24,58 C20,50 12,50 8,58 Z" fill="url(#land)" opacity="0.95" />
    {/* Great Britain */}
    <path d="M64,14 C52,34 58,60 72,84 C80,98 70,120 88,132 C98,120 100,102 94,86 C112,78 106,50 96,38 C90,26 76,10 64,14 Z" fill="url(#land)" />
    {/* Continent (France / Belgium) */}
    <path d="M150,118 C185,104 235,110 300,124 L320,175 L138,175 C138,156 140,134 150,118 Z" fill="url(#land)" />
    {/* Netherlands nub top-right */}
    <path d="M300,58 C282,66 272,88 288,102 C304,96 320,92 320,66 C314,58 306,54 300,58 Z" fill="url(#land)" />
    {/* faint roads */}
    <g stroke="#ffffff" strokeOpacity="0.12" strokeWidth="1">
      <path d="M80,60 L88,96 L150,140" fill="none" />
      <path d="M72,84 L94,86" fill="none" />
    </g>
    {/* city labels */}
    <g fill="#eaf2ff" fontFamily="Plus Jakarta Sans, sans-serif">
      <circle cx="78" cy="56" r="1.6" fill="#cdd8ea" /><text x="82" y="58" fontSize="7.5" opacity="0.85">Birmingham</text>
      <circle cx="60" cy="82" r="1.6" fill="#cdd8ea" /><text x="34" y="79" fontSize="7" opacity="0.8">Cardiff</text>
      <circle cx="88" cy="94" r="2.2" fill="#fff" /><text x="92" y="97" fontSize="9" fontWeight="700">Londra</text>
      <circle cx="80" cy="110" r="1.4" fill="#cdd8ea" /><text x="66" y="120" fontSize="6.5" opacity="0.8">Southampton</text>
      <circle cx="196" cy="132" r="1.4" fill="#cdd8ea" /><text x="200" y="134" fontSize="7" opacity="0.8">Lille</text>
      <text x="150" y="70" fontSize="7" fill="#a9c2dd" opacity="0.6" fontStyle="italic">Manş Denizi</text>
    </g>
  </svg>
);

const MapPin2 = ({ top, left, from, to, initials, pulse }) => (
  <div className="absolute z-10" style={{ top, left }}>
    <div className="relative">
      {pulse && <span className="absolute inset-0 rounded-full ping-soft" style={{ background: to }} />}
      <div className="relative w-6 h-6 rounded-full p-[2px] shadow-md" style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}>
        <div className="w-full h-full rounded-full flex items-center justify-center text-[8px] font-bold text-white" style={{ background: "#12131c" }}>{initials}</div>
      </div>
    </div>
  </div>
);

const Chip = ({ children, active }) => (
  <span className={`flex-shrink-0 rounded-full px-3.5 h-7 flex items-center text-[11px] font-semibold ${active ? "text-white" : "text-white/70 bg-white/8"}`}
    style={active ? { background: "linear-gradient(135deg,#A855F7,#FF3D8B)" } : undefined}>
    {children}
  </span>
);

const StreamThumb = ({ title, place, viewers, rate, g1, g2 }) => (
  <div className="flex-shrink-0 w-[118px] rounded-xl overflow-hidden border border-white/10 relative">
    <div className="h-[74px] relative" style={{ background: `linear-gradient(135deg, ${g1}, ${g2})` }}>
      <div className="absolute inset-0" style={{ background: "radial-gradient(80% 60% at 30% 20%, rgba(255,255,255,0.25), transparent 60%)" }} />
      <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 rounded-md bg-mc-live px-1.5 py-0.5 text-[7px] font-bold text-white">
        <span className="w-1 h-1 rounded-full bg-white" /> LIVE
      </span>
      <span className="absolute top-1.5 right-1.5 inline-flex items-center gap-0.5 rounded-md bg-black/50 px-1.5 py-0.5 text-[7px] font-semibold text-white">
        <Eye className="w-2 h-2" /> {viewers}
      </span>
    </div>
    <div className="p-2 bg-white/[0.04]">
      <p className="text-white text-[10px] font-semibold leading-tight truncate">{title}</p>
      <p className="text-white/45 text-[8.5px] leading-tight mt-0.5">{place} · {rate}/min</p>
    </div>
  </div>
);

const SectionHead = ({ title, sub }) => (
  <div className="px-4 mt-4">
    <h4 className="font-display font-bold text-white text-[13px] leading-none">{title}</h4>
    <p className="text-white/40 text-[9.5px] mt-1">{sub}</p>
  </div>
);

export const DiscoverMockup = ({ className = "" }) => (
  <div className={`relative mx-auto float-y ${className}`}>
    <div className="absolute -inset-10 rounded-full bg-gradient-to-b from-mc-violet/40 via-mc-pink/25 to-transparent blur-3xl opacity-80" aria-hidden />
    <div className="relative w-[292px] rounded-[3rem] border-[11px] border-zinc-950 bg-zinc-950 shadow-[0_40px_120px_-20px_rgba(124,58,237,0.5)]">
      <div className="absolute top-2 left-1/2 -translate-x-1/2 w-24 h-6 bg-black rounded-full z-30" aria-hidden />
      <div className="relative overflow-hidden rounded-[2.2rem] bg-[#08090f] h-[610px] font-body">
        {/* status bar */}
        <div className="flex items-center justify-between px-6 pt-3.5 text-[10px] text-white font-semibold">
          <span className="flex items-center gap-1">12:05 <Navigation className="w-2.5 h-2.5 fill-white" /></span>
          <div className="flex items-center gap-1.5">
            <span className="flex items-end gap-px h-2.5">{[3, 5, 7, 9].map((h, i) => <span key={i} className="w-0.5 bg-white rounded-sm" style={{ height: h }} />)}</span>
            <span className="w-3 h-2.5 rounded-sm border border-white/70 relative flex items-center px-px"><span className="w-full h-1 bg-white rounded-[1px]" /></span>
            <span className="text-[8px]">54</span>
          </div>
        </div>

        {/* title */}
        <h3 className="text-center font-display font-extrabold text-white text-2xl mt-2">Discover</h3>
        <p className="text-center text-white/45 text-[11px] mt-0.5">8 MapCasters online worldwide</p>

        {/* map card */}
        <div className="mx-4 mt-3 relative h-[148px] rounded-2xl overflow-hidden border border-white/10">
          <MiniMap />
          <span className="absolute top-2.5 right-2.5 z-20 inline-flex items-center gap-1.5 rounded-full bg-black/55 backdrop-blur px-2.5 py-1 text-[10px] font-bold text-white">
            <span className="relative flex w-1.5 h-1.5"><span className="absolute inline-flex h-full w-full rounded-full bg-mc-live opacity-75 animate-ping" /><span className="relative inline-flex rounded-full w-1.5 h-1.5 bg-mc-live" /></span>
            3 live now
          </span>
          <MapPin2 top="46%" left="26%" from="#FF3D8B" to="#A855F7" initials="SJ" pulse />
          <MapPin2 top="70%" left="52%" from="#7C3AED" to="#22D3EE" initials="MT" />
          <MapPin2 top="34%" left="82%" from="#F5C542" to="#FF3D8B" initials="ER" pulse />
          <span className="absolute bottom-1.5 left-2 z-20 text-[7px] text-white/70 flex items-center gap-1"><MapPin className="w-2 h-2" /> Maps · Legal</span>
        </div>

        {/* city chips */}
        <div className="flex items-center gap-2 px-4 mt-3 overflow-hidden">
          <Chip>Nearby</Chip>
          <Chip>New York</Chip>
          <Chip active>London</Chip>
          <Chip>Tokyo</Chip>
          <Chip>Paris</Chip>
        </div>

        {/* trending now */}
        <SectionHead title="Trending Now" sub="Hottest streams right now" />
        <div className="flex items-stretch gap-2.5 px-4 mt-2 overflow-hidden">
          <StreamThumb title="Neon night walk" place="London" viewers="1.2k" rate={120} g1="#7C3AED" g2="#FF3D8B" />
          <StreamThumb title="Rooftop sunset" place="Lisbon" viewers="860" rate={90} g1="#F5943C" g2="#FF3D8B" />
          <StreamThumb title="Street jazz" place="Paris" viewers="540" rate={150} g1="#22D3EE" g2="#7C3AED" />
        </div>

        {/* live around the world */}
        <SectionHead title="Live Around the World" sub="Streams from far away" />
        <div className="px-4 mt-2">
          <div className="flex items-center gap-3 rounded-2xl bg-white/[0.04] border border-white/8 px-3 py-2">
            <div className="w-8 h-8 rounded-full p-[2px] flex-shrink-0" style={{ background: "linear-gradient(135deg,#22D3EE,#7C3AED)" }}>
              <div className="w-full h-full rounded-full bg-mc-card flex items-center justify-center text-[10px] font-bold text-white">AK</div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[11px] font-semibold leading-tight">Akira · Shibuya crossing</p>
              <p className="text-white/45 text-[9px] leading-tight">Tokyo · 5,932 mi · 80/min</p>
            </div>
            <span className="rounded-full text-white text-[9px] font-bold px-2.5 py-1" style={{ background: "linear-gradient(135deg,#A855F7,#FF3D8B)" }}>Watch</span>
          </div>
        </div>

        {/* top bounties */}
        <SectionHead title="Top Bounties" sub="Highest rewards from watchers" />
        <div className="px-4 mt-2">
          <div className="flex items-center gap-3 rounded-2xl px-3 py-2.5 border border-mc-pink/20" style={{ background: "linear-gradient(120deg, rgba(168,85,247,0.16), rgba(255,61,139,0.10))" }}>
            <span className="w-8 h-8 rounded-xl bg-mc-pink/20 border border-mc-pink/30 flex items-center justify-center text-mc-pink flex-shrink-0"><Banknote className="w-4 h-4" /></span>
            <div className="flex-1 min-w-0">
              <p className="text-white text-[11px] font-semibold leading-tight">Sunrise at Bondi Beach</p>
              <p className="text-white/50 text-[9px] leading-tight">Sydney · requested by @mara</p>
            </div>
            <span className="inline-flex items-center gap-1 rounded-full bg-mc-gold/15 text-mc-gold text-[10px] font-bold px-2.5 py-1">500 🪙</span>
          </div>
        </div>

        {/* bottom tab bar */}
        <div className="absolute bottom-0 inset-x-0 h-14 bg-[#08090f]/95 border-t border-white/8 backdrop-blur flex items-center justify-around px-5">
          <MapPin className="w-4 h-4 text-white/35" />
          <div className="flex flex-col items-center text-mc-purple"><Compass className="w-4 h-4" /><span className="w-1 h-1 rounded-full bg-mc-purple mt-0.5" /></div>
          <span className="w-11 h-11 -mt-5 rounded-full flex items-center justify-center text-white shadow-[0_8px_24px_rgba(255,61,139,0.5)]" style={{ background: "linear-gradient(135deg,#A855F7,#FF3D8B)" }}><Video className="w-5 h-5" /></span>
          <Wallet className="w-4 h-4 text-white/35" />
          <User className="w-4 h-4 text-white/35" />
        </div>
      </div>
    </div>
  </div>
);

export default DiscoverMockup;
